<template>
  <div class="shell" :class="[`mode-${mode}`]">
    <div class="backdrop" :class="[`mode-${mode}`]">
      <div class="modal" :class="[`mode-${mode}`]" :style="modalStyle">
        <div v-if="mode === 'modal'" class="top" :class="[`mode-${mode}`, { 'top--borderless': !headerBorder }]">
          <div class="title">
            <slot name="header">{{ title }}</slot>
          </div>
          <button v-if="shouldShowCloseButton" class="closeBtn" @click="$emit('close')" aria-label="Close">
            <AppIcon class="icon" name="close" />
          </button>
        </div>
        <div v-if="error" class="errorBanner" role="alert" aria-live="polite">
          <AppIcon class="errorBannerIcon" name="error" />
          <div class="errorBannerText">{{ error }}</div>
        </div>
        <div class="body" :class="[`mode-${mode}`, { 'body--no-padding': !bodyPadding }]" :style="bodyStyle">
          <slot />
        </div>
        <div v-if="shouldRenderFooter" class="footer" :class="[`mode-${mode}`]">
          <div class="footer-start">
            <slot name="footer-start" />
          </div>
          <div class="footer-end">
            <slot name="footer">
              <button class="btn" @click="$emit('close')">Close</button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, useSlots } from 'vue'
import AppIcon from '../../components/AppIcon.vue'

const slots = useSlots()

const props = defineProps({
  title: { type: String, required: true },
  error: { type: String, default: '' },
  mode: { type: String, default: 'modal' },
  maxWidth: { type: [String, Number], default: 600 },
  modalOverflow: { type: String, default: '' },
  bodyOverflow: { type: String, default: '' },
  bodyMinHeight: { type: [String, Number], default: '' },
  bodyMaxHeight: { type: [String, Number], default: '' },
  embeddedFooter: { type: Boolean, default: true },
  headerBorder: { type: Boolean, default: true },
  bodyPadding: { type: Boolean, default: true },
})

const emit = defineEmits(['close'])

const bodyStyle = computed(() => {
  const defaultOverflow = props.mode === 'embedded' ? 'hidden' : 'auto'
  const out = { overflow: String(props.bodyOverflow || defaultOverflow) }
  const mh = props.bodyMinHeight
  if (mh !== '' && mh != null) {
    out.minHeight = typeof mh === 'number' ? `${mh}px` : String(mh)
  }
  const max = props.bodyMaxHeight
  if (max !== '' && max != null) {
    out.maxHeight = typeof max === 'number' ? `${max}px` : String(max)
  }
  return out
})

const modalStyle = computed(() => {
  const defaultOverflow = props.mode === 'embedded' ? 'hidden' : 'visible'
  const out = { overflow: String(props.modalOverflow || defaultOverflow) }
  const mw = props.maxWidth
  if (mw !== '' && mw != null) {
    out.maxWidth = typeof mw === 'number' ? `${mw}px` : String(mw)
  }
  if (props.mode === 'embedded') {
    out.maxWidth = '100%'
    out.width = '100%'
  }
  return out
})

const shouldShowCloseButton = computed(() => props.mode === 'modal')

const shouldRenderFooter = computed(() => {
  if (props.mode === 'modal') return true
  return props.embeddedFooter && Boolean(slots.footer)
})

function handleEscape(e) {
  if (props.mode === 'modal' && e.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Next-UI Modal — restyled with design tokens. Same template + props
   as the legacy Modal so the API is identical; only the visual treatment
   changes (border-radius, typography, shadows, spacing). */

.shell { min-width: 0; }
.shell.mode-embedded { height: 100%; min-height: 0; }

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  padding: var(--space-5);
  z-index: 50;
}
.backdrop.mode-embedded {
  position: static;
  inset: auto;
  background: transparent;
  display: block;
  padding: 0;
  z-index: auto;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.modal {
  width: 100%;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  max-height: calc(100vh - var(--space-7));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal.mode-embedded {
  max-height: none;
  height: 100%;
  min-height: 0;
  box-shadow: none;
  border: none;
  border-radius: 0;
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
}
.top.top--borderless {
  border-bottom: none;
}
.top.mode-embedded { padding: 0 0 var(--space-3); }

.title {
  font-family: var(--font-sans);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  flex: 1;
}

.closeBtn {
  background: none;
  border: none;
  padding: var(--space-1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  color: var(--color-text-tertiary);
  transition: color var(--duration-fast) var(--ease-out),
              background var(--duration-fast) var(--ease-out);
}
.closeBtn:hover {
  background: var(--color-bg-subtle);
  color: var(--color-text-primary);
}
.icon { width: var(--space-4); height: var(--space-4); }

.errorBanner {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin: var(--space-4) var(--space-5) 0;
  padding: var(--space-3);
  border: 1px solid var(--color-danger-soft, rgba(220, 38, 38, 0.28));
  border-radius: var(--radius-md);
  background: var(--color-danger-soft, rgba(220, 38, 38, 0.08));
  color: var(--color-text-primary);
}
.errorBannerIcon {
  width: var(--space-4);
  height: var(--space-4);
  color: var(--color-danger);
  flex: 0 0 auto;
  margin-top: 1px;
}
.errorBannerText {
  min-width: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
}

.body {
  padding: var(--space-5);
  overflow: auto;
  flex: 1 1 auto;
}
.body.body--no-padding {
  padding: 0;
}
.body.mode-embedded {
  padding: var(--space-3);
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.footer {
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border-subtle);
  background: var(--color-bg-app, #FAF6F0);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
}
.footer.mode-embedded {
  padding: var(--space-3);
  flex: 0 0 auto;
}

.footer-start {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}
.footer-start:empty { display: none; }

.footer-end {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-left: auto;
}

</style>
